<template>
  <v-form ref="stripePaymentForm" onsubmit="return false;">
    <template>
      <card
        ref="stripeCard"
        v-if="mountStripeElement"
        dir="ltr"
        :class="[
          'stripe-card pa-3',
          complete ? 'complete' : cardError ? 'invalid' : '',
          darkMode ? 'stripe-card-dark-mode' :'stripe-card-light-mode',
        ]"
        :stripe="DEFINES.STRIPE_KEY"
        :options="_stripeOptions()"
        @change="onCardUpdate"
      />
      <div v-if="cardError" class="red--text">{{ cardError }}</div>
      <v-layout row wrap class="mt-3">
        <v-flex xs12 md8>
          <v-text-field
            outline
            id="address"
            color="secondary"
            :label="$t(`${eventPrefix}.monetization.address.label`)"
            name="address"
            :rules="[rules.required]"
            type="text"
            v-model="address"
            :class="[$vuetify.rtl ? 'pl-2' : 'pr-2']"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 md4>
          <v-text-field
            outline
            id="zip_code"
            color="secondary"
            :label="$t(`${eventPrefix}.monetization.zip_code.label`)"
            name="zip_code"
            :rules="[rules.required, rules.alphaNumericCharacters]"
            type="text"
            v-model="zip_code"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout row wrap class="justify-center">
        <v-flex xs12 md12>
          <div class="text-xs-center">
            <v-btn
              color="primary background--text"
              :class="[$vuetify.rtl ? 'mx-0' : '', 'white--text']"
              @click.stop="onPay"
              :loading="loading"
              :disabled="loading || !complete || !address || !zip_code"
              block
              large
              >{{
                applied_coupon.discount_percentage == 100
                  ? $t(`${eventPrefix}.btn.participate`)
                  : $t('enrollment.steps.payment.button')
              }}
            </v-btn>
          </div>
        </v-flex>
      </v-layout>
    </template>
  </v-form>
</template>

<script>
import rules from '@/validation';
import {
  Card,
  handleCardPayment,
  instance as stripe,
  createToken
} from 'vue-stripe-elements-plus';
import {paymentActionClassifier} from '@/mixins/monetization.js';
import { mapGetters, mapState } from "vuex";
import {mapFields} from "vuex-map-fields"

export default {
  name: 'StripePaymentForm',
  props: {
    mountStripeElement: {
      type: Boolean,
      default: false,
    },
    event_type: {
      type: Number,
      default: null,
    },
    eventPrefix: {
      type: String,
      default: null,
    },
    company: {
      type: Object,
      defualt: null,
    },
    screener_id: {
      type: Number,
      default: null,
    },
    pricing_id: {
      type: Number|String,
      default: null,
    },
    applied_coupon: {
      type: Object,
      default: null,
    },
    coupon_code: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      rules,
      address: null,
      zip_code: null,
      cardError: null,
      complete: false,
      loading: false,
      payment_intent_id: null,
      purchase_id: null,
    };
  },
  components: {
    Card,
  },
  mixins: [paymentActionClassifier],
  computed:{
      ...mapGetters("affiliation", {
          affiliation: "get",
      }),
      ...mapGetters("app", {
        darkMode: "darkMode"
      }),
      ...mapFields("consumer", {
          consumer_uuid: "uuid",
      }),
      ...mapState({
        paymentMethod: state => state.subscription.paymentMethod
      }),
  },
  methods: {
    _stripeOptions(){
      return {
        style: {
          base: {
            lineHeight: '24px',
            fontSize: '16px',
            '::placeholder': {
              color: '#aab7c4',
            },
            color: this.darkMode ? 'white' :''
          },
          invalid: {
            iconColor: 'red',
            color: 'red',
          },
        },
        hidePostalCode: true,
      }
    },

    async _preparePayload() {
      /* Create event type based payload */
      var payload = {};

      (payload.zip_code = this.zip_code),
        (payload.address = this.address),
        (payload.coupon_code = this.coupon_code),
        (payload.charge_type = 'stripe');
      payload.client_secret = this.client_secret;
      payload.consumer_uuid = this.consumer_uuid;

      // Setting affiliation if found
      payload.affiliation = this.affiliation;

      switch (this.event_type) {
        case this.DEFINES.SCREENER_TYPE_VALUE.VIRTUAL:
          payload.pricing_id = this.pricing_id;
          break;
        case this.DEFINES.SCREENER_TYPE_VALUE.PHYSICAL:
          payload.screener_id = this.screener_id;
          payload.consumer_uuid = this.consumer_uuid;
          break;
        case this.DEFINES.SCREENER_TYPE_VALUE.SUBSCRIPTION:
          payload.subscription_plan = this.pricing_id;
          if(this.paymentMethod){
            payload.payment_method_id = this.paymentMethod.id;
          }else{
            let response = await createToken();
            payload.token = response.token;
          }
        break;
        default:
          break;
      }
      return payload;
    },

    async authenticationComplete(event) {
      if (event.data != '3DS-authentication-complete' || !this.purchase_id || !this.payment_intent_id ) {
        return;
      }

      // mark the purchase paid
      try {
        this.loading = true;

        let response = await this.$store.dispatch('monetization/markPaid', {
          purchase_id: this.purchase_id,
          client_secret_id: this.payment_intent_id,
        });
        this.success(response);
      } catch (error) {
        this.failed(error);
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      } finally {
        this.loading = false;
        this.reset();
      }
    },

    async onPay() {
      /* 100% discount will hide the payment form */
      if (
        this.$refs.stripePaymentForm &&
        !this.$refs.stripePaymentForm.validate()
      ) {
        return false;
      }
      this.loading = true;
      const payload = await this._preparePayload();
      const action = this._moduleWithAction();
      try {
        let response = await this.$store.dispatch(action, payload);
        switch (this.event_type) {
          case this.DEFINES.SCREENER_TYPE_VALUE.SUBSCRIPTION:
            /* For Subscription */
            await this.processSubscription(response);
            break;
          default:
            /* For Virtual / Physical / Virtual & Physical / VoD  events*/
            await this.processPurchase(response);
            break;
        }
      } catch (error) {
        this.failed(error);
        this.loading = null;
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      } finally{
        // this.reset();
      }
    },

    async processSubscription(response){
      // First invoice requires 3D Secure / SCA: the payment method is already
      // attached server-side (from the token), so we only authenticate the
      // existing PaymentIntent via Stripe's managed modal.
      if (response.client_secret) {
        const res = await stripe.confirmCardPayment(response.client_secret);

        if (res.error) {
          throw res.error;
        }
        if (res.paymentIntent.status != 'succeeded') {
          throw new Error(res.paymentIntent.status);
        }
        return this.success(response);
      }

      (response.subscription.status == this.DEFINES.SUBSCRIPTION.STATUS.ACTIVE) ? this.success(response) : this.failed(response);
    },

    async processPurchase(response){
      let client_secret = response.purchase.payment_details.stripe_charge_id;
      this.purchase_id = response.purchase.id;

      // Confirm the card and let Stripe handle any required 3D Secure challenge
      // in its own managed modal (this resolves only once authentication completes).
      const res = await handleCardPayment(client_secret);

      // TODO(remove after verifying 3DS): diagnostic only.
      console.log('[stripe] handleCardPayment result', res);

      if (res.error) {
        throw res.error;
      }

      this.payment_intent_id = res.paymentIntent.id;

      //failed
      if (
        res.paymentIntent.status == 'requires_source' ||
        res.paymentIntent.status == 'requires_payment_method'
      ) {
        throw new Error(res.last_payment_error || res.paymentIntent.status);
      }

      // Server-side confirm + mark paid (covers 'succeeded' and post-3DS 'requires_confirmation').
      await this.authenticationComplete({ data: '3DS-authentication-complete' });

      return response;
    },

    success(response = {}) {
      this.$emit('success', response);
    },

    failed(error = {}) {
      this.$emit('failed', error);
    },

    reset() {
      this.payment_intent_id = null;
      this.purchase_id = null;
      this.$refs.stripeCard.clear();
      this.$refs.stripePaymentForm.reset();
      this.$emit('reset');
    },

    onCardUpdate(event) {
      if (event.error) {
        this.cardError = event.error.message;
        return;
      }
      this.cardError = null;
      this.complete = event.complete;
    },
    validate() {
      return this.$refs.stripePaymentForm.validate();
    },
  },
};
</script>

<style>
.stripe-card {
  width: 100%;
  border: 1px solid;
  border-radius: 4px;
}
.stripe-card.light-mode {
  border-color: rgba(0, 0, 0, 0.3);
}
.stripe-card.dark-mode {
  border-color: white;
}
.stripe-card.complete {
  border-color: green;
}
.stripe-card.invalid {
  border-color: red;
}
</style>
